import { isArray } from "lodash";
import SystemConstants from "src/app/util/constants/SystemConstants";
import { AllLocaleWithChild, Locale } from "../../locale/models/locale";
import { ChartTypeList } from "../constants/chart-type-list";
import { ChartType } from "../enums/chart-type";
import { FormulaNumpadValueType } from "../enums/formula-numpad";
import { TrackerDependencyType } from "../enums/tracker-dependency-type";
import Formula from "../models/formula";
import Tracker, { DependentTracker, IndependentTracker } from "../models/tracker";
import TrackerApiModel from "../models/tracker-api-model";
import TrackerType from "../models/tracker-type";
import { TrackerAddFormTemplate } from "../modules/tracker-form-templates/enums/tracker-add-form-template";

export default class TrackerModelsFactory {
    static getTrackerApiModel(tracker: Partial<Tracker>) {
        let trackerApiModel = new TrackerApiModel();
        if(tracker._id)
            trackerApiModel._id = tracker._id;
        if(tracker.calculation) 
            trackerApiModel.calculation = tracker.calculation
        if(tracker.chartAttributes) {
            trackerApiModel.trackerChartAttr = {
                chartBackColor : tracker.chartAttributes.backgroundColor,
                chartType : tracker.chartAttributes.type,
                chartSeriesColor: tracker.chartAttributes.seriesColor
            }
            if(tracker.chartAttributes.backgroundImage) 
                trackerApiModel.trackerChartAttr.chartBackImg = tracker.chartAttributes.backgroundImage
        }
        if(tracker.currentYearTimeBin) 
            trackerApiModel.currentYearTimeBin = tracker.currentYearTimeBin
        if(tracker.dependency && tracker.dependency.selectedType) {
            trackerApiModel.depend = tracker.dependency.selectedType;
            if(tracker.dependency.details) {
                this.setDependencyDetailsFromTrackerViewModel(trackerApiModel, tracker);
            }
        }
        if(tracker.description)
            trackerApiModel.description = tracker.description;
        if(tracker.image)
            trackerApiModel.image = tracker.image
        if (tracker.isDefaultTracker !== undefined && tracker.isDefaultTracker !== null) trackerApiModel.isDefaultTracker = tracker.isDefaultTracker
        if(tracker.locale)
            this.setLocaleFromTrackerViewModel(trackerApiModel, tracker);
        if(tracker.localeList && tracker.localeList.length > 0)
            this.setLocaleListFromTrackerViewModel(trackerApiModel, tracker);
        if(tracker.creatorId)
            trackerApiModel.memberId = tracker.creatorId;
        if(tracker.shortDescription)
            trackerApiModel.shortDescription = tracker.shortDescription;
        if(tracker.status)
            trackerApiModel.status = tracker.status
        if(tracker.publishEndDate)
            trackerApiModel.publishEndDate = tracker.publishEndDate;
        if(tracker.name)
            trackerApiModel.trackerName = tracker.name;
        if(tracker.publishStartDate)
            trackerApiModel.publishStartDate = tracker.publishStartDate;
        if(tracker.trackerTags)
            trackerApiModel.trackerTag = Object.assign([], tracker.trackerTags);
        if(tracker.trackerType && (tracker.trackerType as TrackerType)._id)
            trackerApiModel.trackerTypeId = (tracker.trackerType as TrackerType)._id;
        if(tracker.trackingRight || tracker.trackingRight ===0)
            trackerApiModel.trackingRight = tracker.trackingRight;
        if(tracker.unit && tracker.unit._id)
            trackerApiModel.unitId = tracker.unit._id
        if(tracker.unitType && tracker.unitType._id)
            trackerApiModel.unitTypeId = tracker.unitType._id
        if(tracker.updateInterval)
            trackerApiModel.updateInterval = tracker.updateInterval;
        if(tracker.updateIntervalCustomYearsCount) 
            trackerApiModel.updateIntervalCustomYearsCount = tracker.updateIntervalCustomYearsCount
        return trackerApiModel;
    }

    static setDependencyDetailsFromTrackerViewModel(trackerApiModel: TrackerApiModel, 
        tracker: Partial<Tracker>) {
        let  dependencyMap = {
            [TrackerDependencyType.INDEPENDENT] : () => {
                this.setIndependentFieldsFromTrackerViewModel(trackerApiModel, tracker);
            },
            [TrackerDependencyType.FORMULA] : () => {
                this.setDependentFieldsFromTrackerViewModel(trackerApiModel, tracker);
            },
        }
        if(dependencyMap[tracker.dependency.selectedType])
            dependencyMap[tracker.dependency.selectedType]();
    }

    static setDependentFieldsFromTrackerViewModel(trackerApiModel: TrackerApiModel, 
        tracker: Partial<Tracker>) {
        if(tracker.formulaDetails._id)
            trackerApiModel.formulaId = tracker.formulaDetails._id
        if(tracker.dependsOnTrackers)
            trackerApiModel.dependsId = Object.assign([], tracker.dependsOnTrackers);
    }

    static setFoodTrackerFormFieldsFromApiModel(trackerApiModel: TrackerApiModel, 
        tracker: Partial<Tracker>) {
            let commonNutrition = tracker.commonNutrition;
            if(commonNutrition) {
                    trackerApiModel.commonNutrition = commonNutrition;
                }
    }

    static setIndependentFieldsFromTrackerViewModel(trackerApiModel: TrackerApiModel, 
        tracker: Partial<Tracker>) {
            trackerApiModel.template = tracker.selectedTemplate;
            let formTemplateMap = {
                [TrackerAddFormTemplate.FOOD] : () =>{
                    this.setFoodTrackerFormFieldsFromApiModel(trackerApiModel, tracker);
                },
                [TrackerAddFormTemplate.PUSH] : () => {
                    this.setRangeMoveMinFieldsFromApiModel(trackerApiModel, tracker);
                },
                [TrackerAddFormTemplate.RANGE] : () => {
                    this.setRangeMoveMinFieldsFromApiModel(trackerApiModel, tracker);
                }
            }
            if(tracker.selectedTemplate && formTemplateMap[tracker.selectedTemplate])
                formTemplateMap[tracker.selectedTemplate]();
    }
    
    static setLocaleFromTrackerViewModel(trackerApiModel: TrackerApiModel, 
        tracker: Partial<Tracker>) {
            if(tracker.locale._id === SystemConstants.ALL) {
                const localeId = {all: true, children: [SystemConstants.ALL]} as AllLocaleWithChild
                trackerApiModel.localeId = new AllLocaleWithChild({existingValue: localeId})
            } else {
                const localeId = {all: false, children: [tracker.locale._id]} as AllLocaleWithChild
                trackerApiModel.localeId = new AllLocaleWithChild({existingValue: localeId})
            }
    }

    static setLocaleListFromTrackerViewModel(trackerApiModel: TrackerApiModel, 
        tracker: Partial<Tracker>) {
            if(tracker.localeList === SystemConstants.ALL) {
                const localeId = {all: true, children: [SystemConstants.ALL]} as AllLocaleWithChild
                trackerApiModel.localeId = new AllLocaleWithChild({existingValue: localeId})
            } else {
                const localeId = {all: false, children: [(tracker.localeList[0] as Locale)._id]} as AllLocaleWithChild
                trackerApiModel.localeId = new AllLocaleWithChild({existingValue: localeId})
            }
    }

    static setRangeMoveMinFieldsFromApiModel(trackerApiModel: TrackerApiModel, 
        tracker: Partial<Tracker>) {
            let minMaxValueValues = tracker.minMaxMoveValues;
            if(minMaxValueValues) {
                    trackerApiModel.inputOption = minMaxValueValues;
                }
    }

    static getTrackerViewModel(trackerApiModel: Partial<TrackerApiModel>) {
        let tracker = new Tracker();
        if(trackerApiModel._id) 
            tracker._id = trackerApiModel._id
        if(trackerApiModel.calculation) 
            tracker.calculation = trackerApiModel.calculation
        if(trackerApiModel.trackerChartAttr) {
            tracker.chartAttributes = {
                backgroundColor: trackerApiModel.trackerChartAttr.chartBackColor,
                type: trackerApiModel.trackerChartAttr.chartType as ChartType,
                seriesColor: trackerApiModel.trackerChartAttr.chartSeriesColor,
                typelabel: ChartTypeList[trackerApiModel.trackerChartAttr.chartType],
                backgroundImage: trackerApiModel.trackerChartAttr.chartBackImg
            }
            if(trackerApiModel.trackerChartAttr.chartBackImg)
                tracker.chartAttributes.backgroundImage = trackerApiModel.trackerChartAttr.chartBackImg
        }
        if(trackerApiModel.currentYearTimeBin) 
            tracker.currentYearTimeBin = trackerApiModel.currentYearTimeBin
        if(trackerApiModel.depend) {
            tracker.dependency = {
                selectedType: trackerApiModel.depend
            }
            this.setDependencyDetailsFromTrackerApiModel(tracker, trackerApiModel);
        }
        if(trackerApiModel.description)
            tracker.description = trackerApiModel.description;
        if(trackerApiModel.image)
            tracker.image = trackerApiModel.image
        tracker.isDefaultTracker = trackerApiModel.isDefaultTracker       
        tracker.isSystemTracker = trackerApiModel.isSystemTracker
        if(trackerApiModel.locales)
            this.setLocaleFromTrackerAPIModel(tracker, trackerApiModel);
        if(trackerApiModel.memberId)
            tracker.creatorId = trackerApiModel.memberId
        if(trackerApiModel.shortDescription)
            tracker.shortDescription = trackerApiModel.shortDescription;
        if(trackerApiModel.status)
            tracker.status = trackerApiModel.status
        if(trackerApiModel.publishEndDate)
            tracker.publishEndDate = trackerApiModel.publishEndDate;
        if(trackerApiModel.trackerName)
            tracker.name = trackerApiModel.trackerName;
        if(trackerApiModel.publishStartDate)
            tracker.publishStartDate = trackerApiModel.publishStartDate;
        if(trackerApiModel.trackerTag)
            tracker.trackerTags = Object.assign([], trackerApiModel.trackerTag);
        if(trackerApiModel.trackerTypeId)
            tracker.trackerType = { _id: trackerApiModel.trackerTypeId};
        if(trackerApiModel.trackerType)
            tracker.trackerType = trackerApiModel.trackerType
        if(trackerApiModel.trackingRight)
            tracker.trackingRight = trackerApiModel.trackingRight;
        if(trackerApiModel.unitId)
            tracker.unit = { _id: trackerApiModel.unitId};
        if(trackerApiModel.unit && isArray(trackerApiModel.unit) && trackerApiModel.unit.length > 0)
            tracker.unit = {...trackerApiModel.unit[0]}
        if(trackerApiModel.unitTypeId)
            tracker.unitType = { _id: trackerApiModel.unitTypeId};
        if(trackerApiModel.unitType && trackerApiModel.unitType._id)
            tracker.unitType = trackerApiModel.unitType
        if(trackerApiModel.updateInterval)
            tracker.updateInterval = trackerApiModel.updateInterval;
        if(trackerApiModel.updateIntervalCustomYearsCount)
            tracker.updateIntervalCustomYearsCount = trackerApiModel.updateIntervalCustomYearsCount;
        return tracker;
    }

    static setDependencyDetailsFromTrackerApiModel(tracker: Tracker, trackerApiModel: Partial<TrackerApiModel>) {
        let  dependencyMap = {
            [TrackerDependencyType.INDEPENDENT] : () => {
                this.setIndependentFieldsFromTrackerAPIModel(tracker, trackerApiModel);
            },
            [TrackerDependencyType.FORMULA] : () => {
                this.setDependentFieldsFromTrackerAPIModel(tracker, trackerApiModel);
            },
        }
        if(dependencyMap[trackerApiModel.depend])
            dependencyMap[trackerApiModel.depend]();
    }

   static setDependentFieldsFromTrackerAPIModel(tracker: Tracker, 
        trackerApiModel: Partial<TrackerApiModel>) {
            tracker.dependency.details = new DependentTracker();
            
            if(trackerApiModel.formulaId)
                tracker.dependency.details.formula = { _id: trackerApiModel.formulaId};                
            if(trackerApiModel.formula && isArray(trackerApiModel.formula) && trackerApiModel.formula.length > 0)
                tracker.dependency.details.formula = {...trackerApiModel.formula[0]};
            if(trackerApiModel.dependsId)
                tracker.dependency.details.dependsOnTrackers = Object.assign([], trackerApiModel.dependsId);
    }

    static setIndependentFieldsFromTrackerAPIModel(tracker: Tracker, 
        trackerApiModel: Partial<TrackerApiModel>) {
            tracker.dependency.details = new IndependentTracker()
            tracker.selectedTemplate = trackerApiModel.template as TrackerAddFormTemplate;
            let formTemplateMap = {
                [TrackerAddFormTemplate.FOOD] : () =>{
                    if(trackerApiModel.commonNutrition)
                        tracker.commonNutrition = trackerApiModel.commonNutrition;
                },
                [TrackerAddFormTemplate.PUSH] : () => {
                    if(trackerApiModel.inputOption)
                        tracker.pushFormValues  = trackerApiModel.inputOption;
                },
                [TrackerAddFormTemplate.RANGE] : () => {
                    if(trackerApiModel.inputOption)
                        tracker.rangeFormValues  = trackerApiModel.inputOption;
                }
            }
            if(formTemplateMap[tracker.selectedTemplate])
                formTemplateMap[tracker.selectedTemplate]();
    }

    static setLocaleFromTrackerAPIModel(tracker: Tracker, 
        trackerApiModel: Partial<TrackerApiModel>) {
            if((trackerApiModel.localeId as AllLocaleWithChild).all) {
                tracker.localeList = SystemConstants.ALL;
            } else {
                tracker.localeList = Object.assign([], trackerApiModel.locales);
            }
    }
    
    static setRangeMoveMinFieldsFromViewModel(tracker: Tracker, 
        trackerApiModel: Partial<TrackerApiModel>) {
            let minMaxValueValues = tracker.minMaxMoveValues;
            if(minMaxValueValues) {
                    trackerApiModel.inputOption = minMaxValueValues;
                }
    }

    
    static getDependsOnTrackers(formula: Formula) {
        if(formula.params && formula.params.length > 0)
            return  formula.params.reduce((reducedArray, param) => (param.type === FormulaNumpadValueType.TRACKER && 
                reducedArray.push(param.trackerId), reducedArray), [])
        else
            return [];
    }

}